import {
  DrmIssueResponse,
  getSelectedDrm,
  isbot,
  Orientation,
  PlayerMode,
  PlayResponse,
  request,
  Service,
  supportedFormats,
} from "@tv4/avod-web-player-common";
import {
  getBrowserName,
  isAndroid,
  isChromecast,
  isIOS,
  isIpadOS,
} from "@tv4/avod-web-player-device-capabilities";

export const DEFAULT_CAPABILITIES = [
  "live-drm-adstitch-2",
  "yospace3",
  "prism",
];

type TDevice = "chromecast" | "browser-ios" | "browser-android" | "browser";

function getDevice(): TDevice {
  if (isChromecast()) return "chromecast";
  if (isIOS() || isIpadOS()) return "browser-ios";
  if (isAndroid()) return "browser-android";
  return "browser";
}

type PlaybackAPIOptions = {
  url: string;
  getAccessToken: () => string | undefined;
  includeCredentials?: boolean;
  playerMode?: PlayerMode;
  service: Service;
  sessionId?: string;
  gdprConsent?: string;
  anonymousPID?: string;
};

export class PlaybackAPI {
  private baseUrl: string;
  private includeCredentials: boolean;
  public getAccessToken: () => string | undefined;
  private destroyed = false;

  private protocols = supportedFormats();
  private playerMode: PlayerMode;
  private service: Service;
  private device = getDevice();
  private gpdrConsent?: string;
  private anonymousPID?: string;

  constructor(options: PlaybackAPIOptions) {
    this.includeCredentials = !!options.includeCredentials;
    this.baseUrl = options.url;
    this.getAccessToken = options.getAccessToken;
    this.playerMode = options.playerMode || PlayerMode.DEFAULT;
    this.service = options.service;
    this.gpdrConsent = options.gdprConsent;
    this.anonymousPID = options.anonymousPID;
  }

  private async request<T>(url: URL): Promise<T> {
    if (await isbot()) {
      return new Promise(() => {
        // promise will never resolve for bot
      });
    } else {
      const [response, error] = await request<T>(
        url,
        {
          method: "GET",
          credentials: this.includeCredentials ? "include" : "same-origin",
        },
        this.getAccessToken,
        () => this.destroyed
      );

      if (error || !response) {
        throw error;
      }
      return response;
    }
  }

  private async getUrl(path: string): Promise<URL> {
    const url = new URL(path, this.baseUrl);

    url.searchParams.append("service", this.service);
    url.searchParams.append("device", this.device || getDevice());
    url.searchParams.append("protocol", this.protocols.join(","));
    url.searchParams.append("drm", (await getSelectedDrm()) ?? "");
    url.searchParams.append("browser", getBrowserName());

    return url;
  }

  public async playRequest(options: {
    assetId: string;
    sessionId: string;
  }): Promise<PlayResponse> {
    const url = await this.getUrl(`play/${options.assetId}`);

    url.searchParams.append("capabilities", DEFAULT_CAPABILITIES.join(","));
    url.searchParams.append(
      "preview",
      this.playerMode === PlayerMode.PREVIEW ? "true" : "false"
    );

    url.searchParams.append("wire_session_id", options.sessionId);
    if (this.gpdrConsent) {
      url.searchParams.append("gdpr_consent", this.gpdrConsent);
    }
    if (this.anonymousPID) {
      url.searchParams.append("anon_id", this.anonymousPID);
    }

    return this.request<PlayResponse>(url);
  }

  public async shortRequest(
    id: string,
    orientation: Orientation
  ): Promise<PlayResponse> {
    const url = await this.getUrl(`play/${id}/short`);

    url.searchParams.append("orientation", orientation);
    url.searchParams.append(
      "preview",
      this.playerMode === PlayerMode.PREVIEW ? "true" : "false"
    );

    return this.request<PlayResponse>(url);
  }

  public async drmRequest(id: string): Promise<DrmIssueResponse> {
    const url = await this.getUrl(`drm/${id}/issue`);
    return this.request<DrmIssueResponse>(url);
  }

  public destroy() {
    this.destroyed = true;
  }
}
